import type React from 'react';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './button.module.scss';

export type WordsHappenButtonVariant = 'default' | 'primary' | 'coral' | 'sun' | 'paper' | 'ink' | 'ghost-dashed';

export type WordsHappenButtonSize = 'sm' | 'md' | 'lg';

export interface WordsHappenButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: WordsHappenButtonVariant;
  size?: WordsHappenButtonSize;
  icon?: React.ReactNode;
  loading?: boolean;
}

// `null` means the variant never casts the Meadow 3D shadow, so it has no press travel either.
const SHADOW_COLOR: Record<WordsHappenButtonVariant, string | null> = {
  default: 'var(--wh-ink)',
  primary: 'var(--wh-teal-deep)',
  coral: 'var(--wh-ink)',
  sun: 'var(--wh-ink)',
  paper: 'var(--wh-ink)',
  ink: 'var(--wh-ink)',
  'ghost-dashed': null,
};

const VARIANT_CLASS: Record<WordsHappenButtonVariant, string> = {
  default: styles.variantDefault,
  primary: styles.variantPrimary,
  coral: styles.variantCoral,
  sun: styles.variantSun,
  paper: styles.variantPaper,
  ink: styles.variantInk,
  'ghost-dashed': styles.variantGhostDashed,
};

const SIZE_CLASS: Record<WordsHappenButtonSize, string> = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
};

export const WordsHappenButton: React.FC<WordsHappenButtonProps> = ({
  variant = 'default',
  size = 'md',
  icon,
  loading = false,
  disabled,
  children,
  className,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  ...rest
}) => {
  const { theme } = useWordsHappenTheme();
  const [pressed, setPressed] = useState(false);

  const isMeadow = theme === 'meadow';
  const isDisabled = disabled || loading;
  const shadowColor = SHADOW_COLOR[variant];
  const isPressed = isMeadow && shadowColor !== null && pressed && !isDisabled;

  const shadowValue = isMeadow && shadowColor !== null ? `0 ${isPressed ? 1 : 3}px 0 ${shadowColor}` : undefined;

  const cls = [
    styles.btn,
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    !isMeadow ? styles.flat : null,
    isMeadow ? styles.meadow : null,
    isPressed ? styles.pressed : null,
    loading ? styles.loading : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={cls}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      style={shadowValue !== undefined ? ({ '--btn-shadow': shadowValue } as CSSProperties) : undefined}
      onPointerDown={(e) => {
        setPressed(true);
        onPointerDown?.(e);
      }}
      onPointerUp={(e) => {
        setPressed(false);
        onPointerUp?.(e);
      }}
      onPointerLeave={(e) => {
        setPressed(false);
        onPointerLeave?.(e);
      }}
      {...rest}
    >
      {loading && <span className={styles.spinner} role="status" aria-label="Loading" />}
      <span className={styles.label} aria-hidden={loading || undefined}>
        {icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        {children}
      </span>
    </button>
  );
};
